import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook for URL-persisted search + filters.
 * Reduces boilerplate when adding search and filter persistence to list pages.
 *
 * @param {Object} config
 * @param {Object} config.filterParamKeys - e.g. OLYMPIAD_FILTER_PARAM_KEYS or FILTER_PARAM_KEYS
 * @param {string} config.searchParamKey - URL param for search (e.g. "search")
 * @param {Function} config.readFiltersFromUrl - () => ({ search, ...filters }) - parse URL to state
 * @param {Function} [config.onUrlChange] - (data) => void - called when URL changes (browser back/forward). Use to sync state: setCreatedDateFrom(data.createdDateFrom), etc.
 * @param {number} [config.debounceMs=300] - search debounce delay
 *
 * @returns {{ search, setSearch, searchQuery, updateFilterParams, clearFilterParams }}
 */
export function useUrlFilters({
  filterParamKeys,
  searchParamKey,
  readFiltersFromUrl,
  onUrlChange,
  debounceMs = 300,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramKey = searchParamKey ?? filterParamKeys?.SEARCH ?? "search";

  const [search, setSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    return p.get(paramKey) || "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    return p.get(paramKey) || "";
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    const prev = prevDebouncedSearchRef.current;
    prevDebouncedSearchRef.current = debouncedSearch;
    if (prev === debouncedSearch) return;
    setSearchParams((prevParams) => {
      const next = new URLSearchParams(prevParams);
      if (debouncedSearch?.trim()) {
        next.set(paramKey, debouncedSearch.trim());
      } else {
        next.delete(paramKey);
      }
      next.set("current-page", "1");
      return next;
    });
  }, [debouncedSearch, setSearchParams, paramKey]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!isInitialMount.current && readFiltersFromUrl) {
      const data = readFiltersFromUrl();
      setSearch(data.search ?? "");
      setDebouncedSearch(data.search ?? "");
      onUrlChange?.(data);
    } else {
      isInitialMount.current = false;
    }
  }, [searchParams, readFiltersFromUrl, onUrlChange]);

  const updateFilterParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            next.set(key, String(value));
          } else if (key) {
            next.delete(key);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const clearFilterParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (filterParamKeys) {
        Object.values(filterParamKeys).forEach((key) => next.delete(key));
      }
      return next;
    });
  }, [setSearchParams, filterParamKeys]);

  return {
    search,
    setSearch,
    searchQuery: debouncedSearch?.trim() || undefined,
    updateFilterParams,
    clearFilterParams,
    searchParams,
  };
}
